import { isString, forEach, findIndex, reduce } from 'lodash';

import { isArray, OwnedObject, invalidateOwner } from 'gear';

import { Xml } from '../util';
import { rgb2lch, rgb2lab, rgb2xyz } from './color-space';

const _componentToHex = function(c) {
  const hex = c.toString(16).toUpperCase();
  return hex.length === 1 ? "0" + hex : hex;
};

const _hexToInt = function(hex) {
  return parseInt(hex,16);
};

const _floatRepToInt = function(v) {
  return Math.floor(v*255);
};

export const Color = OwnedObject.extend({ typeName: 'Color', __debugProps: true,
  Properties: {
    alpha         : { type:'number' , def: 1.0                        , dropDef:true, onChange: invalidateOwner }, // 0...1.0
    rgb           : { type:'string' , def:'000000' , tag: 'RGB'                     , onChange: invalidateOwner }, // RRGGBB
    cmyk          : { type:'string' , def:null     , tag: 'CMYK'      , dropDef:true, onChange: invalidateOwner }, // CCMMYYKK
    lab           : { type:'string' , def:null     , tag: 'LAB'       , dropDef:true, onChange: invalidateOwner }, 
    name          : { type:'string' , def:null                        , dropDef:true, onChange: invalidateOwner },
    spotColorName : { type:'string' , def:null                        , dropDef:true, onChange: invalidateOwner },
    showThrough   : { type:'boolean', def:false                       , dropDef:true, onChange: invalidateOwner },
    tag           : { type:'string' , def:null                        , dropDef:true, onChange: invalidateOwner },
    label         : { type:'string' , def:null                        , dropDef:true, onChange: invalidateOwner },
    tint          : { type:'number' , def: 1.0                        , dropDef:true, onChange: invalidateOwner }, // 0...1.0
    printRgb      : { type:'string' , def:null     , tag: 'PrintRGB'                , onChange: invalidateOwner }, // RRGGBB
    colorType     : { type:'string' , def:null     , tag: 'ColorType'               , onChange: invalidateOwner }, // RGB/CMYK/CE/LAB/GRAY
    ceRGBHigh     : { type:'string' , def:null     , tag: 'CERGBHigh', dropDef:true , onChange: invalidateOwner }, // RRGGBB
    ceRGBMid      : { type:'string' , def:null     , tag: 'CERGBMid' , dropDef:true , onChange: invalidateOwner }, // RRGGBB
    ceRGBShad     : { type:'string' , def:null     , tag: 'CERGBShad', dropDef:true , onChange: invalidateOwner }, // RRGGBB
    printCERGBHigh: { type:'string' , def:null     , tag: 'PrintCERGBHigh', dropDef:true , onChange: invalidateOwner }, // RRGGBB
    printCERGBMid : { type:'string' , def:null     , tag: 'PrintCERGBMid' , dropDef:true , onChange: invalidateOwner }, // RRGGBB
    printCERGBShad: { type:'string' , def:null     , tag: 'PrintCERGBShad', dropDef:true , onChange: invalidateOwner }, // RRGGBB
    gray          : { type:'number' , def:null                        , dropDef:true, onChange: invalidateOwner }, // 0...1.0
    profile       : { type:'string' , def:null     , tag: 'Profile'  , dropDef:true , onChange: invalidateOwner },
  },

  init( config, alpha ) {
    this.callParent(config);
    if( isString(config) ) {
      this._rgb = config;
      if( alpha ) {
        this._alpha = alpha;
      }
    }
  },

  // [TODO][PROPS]...

  byteAlpha() {
    return _floatRepToInt(this._alpha);
  },

  stringAlpha() {
    return _componentToHex(this.byteAlpha());
  },

  get lch() { return rgb2lch( this.rgbArray ); },
  //get lab() { return rgb2lab( this.rgbArray ); },
  get xyz() { return rgb2xyz( this.rgbArray ); },

  get rgba() { return `rgba(${this.r()},${this.g()},${this.b()},${this._alpha})`; },
  get argb() { return this.stringAlpha()+this._rgb.toUpperCase(); },
  get rgbHex() { return _hexToInt(this._rgb); },

  get rgbArray() { return [ this.r(), this.g(), this.b() ]; },

  red( v ) {
    if( v === undefined ) {
      return this.r()/255;
    }
    else {
      return this.r(_floatRepToInt(v));
    }
  },
  green( v ) {
    if( v === undefined ) {
      return this.g()/255;
    }
    else {
      return this.g(_floatRepToInt(v));
    }
  },
  blue( v ) {
    if( v === undefined ) {
      return this.b()/255;
    }
    else {
      return this.b(_floatRepToInt(v));
    }
  },
  acmyk() { return this.stringAlpha()+this._cmyk.toUpperCase(); },
  svgColor() { return "#" + this._rgb.toUpperCase(); },
  svgOpacity() { return this._alpha ; },

  rr() { return this._rgb.substring(0,2); },
  gg() { return this._rgb.substring(2,4); },
  bb() { return this._rgb.substring(4,6); },

  r( v ) {
    if( v === undefined ) {
      return _hexToInt(this.rr());
    }
    else {
      const rr = _componentToHex(v);
      this._rgb = rr + this._rgb.substring(2,6);
      return this;
    }
  },
  g( v ) {
    if( v === undefined ) {
      return _hexToInt(this.gg());
    }
    else {
      const gg = _componentToHex(v);
      const rgb = this._rgb;
      this._rgb = rgb.substring(0,2) + gg + rgb.substring(4,6);
      return this;
    }
  },
  b( v ) {
    if( v === undefined ) {
      return _hexToInt(this.bb());
    }
    else {
      const bb = _componentToHex(v);
      this._rgb = this._rgb.substring(0,4) + bb;
      return this;
    }
  },

  isEqual ( other) { return this.propertiesAreEqual(other); },
  toOldXmlString() { return this.argb + this._name?("-"+this._name):""; },
  textualize ( includeTag) { if( includeTag===undefined) { includeTag=true; }
    let t = '' ;
    const a = this.stringAlpha();
    if( a !== 'FF'  ) { t += a + ' '; }
    if( this._rgb  ) { t += this._rgb.toUpperCase() + ' '; }
    if( this._cmyk ) { t += this._cmyk.toUpperCase() + ' '; }
    if( this._lab ) { t += this._lab.toUpperCase() + ' '; }
    if( this._ceRGBHigh  ) { t += this._ceRGBHigh.toUpperCase() + ' '; }
    if( this._ceRGBMid  ) { t += this._ceRGBMid.toUpperCase() + ' '; }
    if( this._ceRGBShad  ) { t += this._ceRGBShad.toUpperCase() + ' '; }
    if( this._profile  ) { t += this._profile.toUpperCase() + ' '; }
    if( this._name ) { t += this._name ; }
    if( this._spotColorName ) { t += this._spotColorName ; }
    if( this._showThrough ) { t += ' ST '; }
    if( includeTag && this._tag ) { t += ' '+this._tag; }
    if( this._tint ) { t += this._tint.toString() + ' '; }
    if( this._gray ) { t += this._gray.toString() + ' '; }
    return t;
  },
  replace( color,keepTag,keepLabel) {
    this._replace(color,keepTag,keepLabel);
    this.invalidate();
  },

  invalidate: invalidateOwner,

// Use this function only if you know what you are doing
// It replaces the color without invalidating the owner.
// Unique color zones machinery use this to avoid reprocessing
// figures by also updating the output representations.
  _replace( color, keepTag = true, keepLabel = true ) {

    const oldTag   = this._tag;
    const oldLabel = this._label;

    this.copyProperties(color);

    if( keepTag ) {
      this._tag = oldTag;
    }
    if( keepLabel ) {
      this._label = oldLabel;
    }
  }
});
Xml.addFromBuild(Color,'Color');
Xml.addToXml(Color);

Color.Transparent = function() {
  return new Color({ alpha: 0, rgb:'000000' });
};
Color.Black = function() {
  return new Color({ rgb: '000000' });
};
Color.Gray = function() {
  return new Color({ rgb: '888888' });
};
Color.White = function() {
  return new Color({ rgb: 'FFFFFF' });
};
Color.CutPen = function() {
  return new Color({ alpha: 0.5, rgb: '00FF00', name: 'CutContour' });
};

Color.RGB = Color.fromRgb = function( rgb, alpha = 1 ) {
  return new Color({ rgb, alpha });
};
//shouldn't accept float type 
function componentToHex( c ) {
  const hex = Math.round(c).toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}
Color.fromImageData = function( data ) {
  const r = data[0], g = data[1], b = data[2], a = data[3];
  return new Color({ rgb: ( componentToHex(r) + componentToHex(g) + componentToHex(b) ), alpha: a/255 });
};


Color.areEqual = function(ca,cb,includeTag) {
  return ca.textualize(includeTag) === cb.textualize(includeTag);
};

Color.lumDiff = function(ca,cb) {
  // See http://juicystudio.com/article/luminositycontrastratioalgorithm.php

  const la = 0.2126 * Math.pow(ca.red()  , 2.2) +
           0.7152 * Math.pow(ca.green(), 2.2) +
           0.0722 * Math.pow(ca.blue() , 2.2);

  const lb = 0.2126 * Math.pow(cb.red()  , 2.2) +
           0.7152 * Math.pow(cb.green(), 2.2) +
           0.0722 * Math.pow(cb.blue() , 2.2);

  return la > lb ? ( (la+0.05) / (lb+0.05) )
                 : ( (lb+0.05) / (la+0.05) );
};

Color.changeLuminosity = function(c,lum) {
  const cc = c.clone();
  cc.red( Math.min(Math.max(0,cc.red() * (1+lum) ),1) );
  cc.green( Math.min(Math.max(0,cc.green() * (1+lum) ),1) );
  cc.blue( Math.min(Math.max(0,cc.blue() * (1+lum) ),1) );
  return cc;
};

Color.highestContrast = function(color,pool) {
  if( pool === undefined ) {
    pool = [ Color.White(), Color.Black() ];
  }

  let r, rDiff = 0;
  forEach( pool, c => {
    const d = Color.lumDiff(color,c);
    if( d > rDiff ) {
      rDiff = d;
      r = c;
    }
  });
  return r;
};

Color.removeHash = function(color) { return color.replace('#', ''); },

Color.arrayFromXml = function(coloredNode) {
  const list = Xml.getObjectArray(coloredNode,Color,'Color');
  return list.length > 0 ? list : Color.arrayFromXmlString(coloredNode);
};
Color.arrayFromXmlString = function(coloredNode) {
  const list = [];
  const colorsString = coloredNode.getAttribute('Colors') ;
  if( colorsString ) {
    const colors = colorsString.split(',') ;
    // Add ColorsAlpha ?
    for( let k = 0, kEnd=colors.length ; k < kEnd ; ++k ) {
      list.push( Color.fromXmlAttr(colors[k]) );
    }
  }
  return list ;
};
Color.fromXml = function(coloredNode) {
  const node = Xml.child(coloredNode,'Color');
  if( node ) {
    return Color.parseXml(node) ;
  }
  else {
    // Backward compatibility
    const colorString = coloredNode.getAttribute('Color') ;
    if( colorString ) {
      const alpha = Xml.getFloatAttr(coloredNode,'Alpha',255)/255.0 ;
      return Color.fromXmlAttr(colorString,alpha);
    }
  }
  return null ;
};
Color.fromXmlString = function(xmlString) {
  const domFigure = Xml.fromString(xmlString);
  return Color.fromXml(domFigure.documentElement);
};

Color.fromXmlAttr = function(xmlString,alpha) {
  if(!xmlString) {
    return Color.Transparent() ;
  }
  const p = {};
  if(alpha===undefined) { alpha = 1.0; }
  p.alpha = alpha ;
  const colorAndName = xmlString.split('-');
  if(colorAndName.length > 1 ) {
    p.name = colorAndName[1] ;
  }
  const parts = colorAndName[0].split('|') ;
  if( parts.length === 1 ) {
    if( parts[0].length === 8 ) {
      p.rgb = parts[0].substring(2);
    }
    else {
      p.rgb = parts[0] ;
    }
  }
  else if( parts.length === 2 ) {
    p.rgb = parts[1] ;
  }
  else if( parts.length === 3 ) {
    p.cmyk = parts[1] ; p.rgb = parts[2] ;
  }
  return new Color(p) ;
};

const _getThingColors = function(thing,colors) {
  return thing.getColors(colors);
};
const _reduceThingColors = function(colors,thing) {
  return thing.getColors(colors);
};

Color.getColors = function(things,colors) {
  if( isArray(things) ) {
    return reduce(things,_reduceThingColors,[]);
  }
  else {
    return _getThingColors(things,colors);
  }
};

const collapseZones = function(colors,options,zones) { if(zones===undefined) { zones = []; }
  const collapseTags = ( options && options.collapseTags !== undefined ) ? options.collapseTags : false;
  const includeTag = ! collapseTags;
  const map = {};
  const isShowThrough = function(z) {return z.value._showThrough;};
  //_.forEach( zones, z => {
  for(let i=0,iEnd=zones.length;i<iEnd;++i) {
    const z = zones[i];
    map[z.value.textualize(includeTag)] = z.value;
  }//);
  //_.forEach(colors,function(c) {
  for(let k=0,kEnd=colors.length;k<kEnd;++k) {
    const c = colors[k];
    const cs = c.textualize(includeTag) ;
    if( ! ( cs in map ) ) {
      map[cs] = c ;
      const zone = { value:c.clone(), type: 'Combined', '_unique':true, tagsCollapsed: collapseTags };

      // ShowThrough zones goes always to the end
      if( c._showThrough ) {
        zones.push(zone);
      }
      else {
        const n = findIndex(zones,isShowThrough); // [TODO]
        if(n !== -1) {
          zones.splice(n,0,zone);
        }
        else {
          zones.push(zone);
        }
      }
    }
  }//);
  return zones;
};

Color.zones = function(things,options,zones) {
  return collapseZones(Color.getColors(things),options,zones);
};

// This one doesn't invalidate the model, use with care
Color.fastReplace = function(color,newColor,keepTags,keepLabels) {
  color._replace(newColor,keepTags,keepLabels); // avoid figure invalidation
};

Color.replace = function(color,newColor,keepTags,keepLabels) {
  color.replace(newColor,keepTags,keepLabels);
};

const _replaceAll = function(colors,replaceFunc,oldColor,newColor,options) {
  let changed = false;
  if( colors.length === 0 ) {
    // Some figures like full color rasters, may not have any colors to replace.
    // In this case, we just avoid going forward here so the figure is not invalidated
    return changed;
  }
  oldColor = oldColor.clone(); // Guard against replaceColor(ownedColor,...)
  const keepTags      = options && options.keepTags      !== undefined ? options.keepTags      : false ;
  const keepLabels    = options && options.keepLabels    !== undefined ? options.keepLabels    : false ;
  const tagsCollapsed = options && options.tagsCollapsed !== undefined ? options.tagsCollapsed : false ;
  const includeTag = ! tagsCollapsed;
  const oldColorString = oldColor.textualize(includeTag);
  //_.forEach(colors,function(c) {
  for(let k=0,kEnd=colors.length;k<kEnd;++k) {
    const c = colors[k];
    const cString = c.textualize(includeTag);
    if( cString === oldColorString ) {
      replaceFunc(c,newColor,keepTags,keepLabels);
      changed = true;
    }
  }//);
  return changed;
};

const replaceThingColor = function(thing,oldColor,newColor,options) {
  return _replaceAll(_getThingColors(thing),Color.replace,oldColor,newColor,options);
};

Color.replaceAll = function(things,oldColor,newColor,options) {
  return _replaceAll(Color.getColors(things),Color.replace,oldColor,newColor,options);
};

const changeThingZone = function(thing,zone,newValue) {
  // [TODO] Avoid changing code wiring for the moment, clean up
  if( thing.changeColorZone ) {
    return thing.changeColorZone(zone,newValue);
  }

  // [TODO] No need for all this checks, replace for assert later
  if( zone._unique ) {
    if( zone.value instanceof Color ) {
      if( newValue instanceof Color ) {
        return replaceThingColor(thing,zone.value,newValue,{tagsCollapsed:zone.tagsCollapsed,keepTags:true});
      }
    }
  }
};

Color.changeZone = function(things,zone,newValue) {
  if( isArray(things) ) {
    for(let k=0,kEnd=things.length;k<kEnd;++k) {
      changeThingZone(things[k],zone,newValue);
    }//);
    return things;
  }
  else {
    return changeThingZone(things,zone,newValue);
  }
};

// [TODO] This should be a mixin, hacking it for now for the release

Color._this_changeZone = function(zone,newValue) {
  return changeThingZone(this,zone,newValue);
};

Color._this_zones = function(options,zones) {
  return collapseZones(_getThingColors(this),options,zones);
};

Color._this_replaceColor = function(oldColor,newColor,options) {
  this.lock();
  return _replaceAll(_getThingColors(this),Color.replace,oldColor,newColor,options);
};

Color._this_fastReplaceColor = function(oldColor,newColor,options) {
  this.lock();
  return _replaceAll(_getThingColors(this),Color.fastReplace,oldColor,newColor,options);
};

Xml.Parser['Color'  ] = Xml.makeObjectParser(Color);
Xml.Parser['Color[]'] = Xml.makeArrayObjectParser(Color);



/** WEBPACK FOOTER **
 ** ./src/cdl/style/color.js
 **/